import React from "react";
import { useMatch, useNavigate, useParams } from "react-router-dom";

import { useQueryGetCourseUnits, useQueryGetUnit } from "@examspark/common-app";
import { Text, Button, RouterLink, Tabs, Toolbar } from "@examspark/common-ui";
import {
  IconPageNextLine,
  IconPagePreviousLine,
} from "@examspark/common-ui/dist/components/icons";
import { ParsedClassroom } from "@examspark/types";

import ClassroomLayout from "../../components/classroom/ClassroomLayout";
import ClassroomUnitActivities from "../../components/classroom/units/ClassroomUnitActivities";
import ClassroomUnitOverview from "../../components/classroom/units/ClassroomUnitOverview";
import ClassroomUnitResources from "../../components/classroom/units/ClassroomUnitResources";
import ClassroomUnitSidebar from "../../components/classroom/units/ClassroomUnitSidebar";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import PageHead from "../../components/common/PageHead";

const ClassroomUnitRoute = (props: { classroom?: ParsedClassroom }) => {
  const navigate = useNavigate();
  const match = useMatch("/classroom/:id/unit/:unitId/:tab/*");
  const params = useParams<{
    id: string;
    unitId: string;
  }>();

  const { data: unit } = useQueryGetUnit(params.unitId, "unit#v0");
  const { data: courseUnits } = useQueryGetCourseUnits(
    props.classroom?.courseId,
  );

  return (
    <ClassroomLayout
      main={
        <>
          <PageHead>
            <Breadcrumbs
              items={[
                ...Breadcrumbs.Classroom(props.classroom),
                <RouterLink key="units" to={`/classroom/${params.id}/units`}>
                  Units / Modules
                </RouterLink>,
              ]}
            />
            <Text.H1>{unit?.name ?? "Loading Unit..."}</Text.H1>
            <Toolbar>
              <Button
                variant="outlined"
                title="Previous"
                onClick={() => {
                  const unitIndex = courseUnits?.findIndex(
                    (u) => u.id === params.unitId,
                  );
                  if (unitIndex !== undefined && unitIndex > 0) {
                    navigate(
                      `/classroom/${params.id}/unit/${
                        courseUnits?.[unitIndex - 1].id
                      }/${match?.params.tab ?? "overview"}`,
                    );
                  }
                }}
                icon={<IconPagePreviousLine />}
              />
              <Button
                variant="outlined"
                title="Next"
                onClick={() => {
                  const unitIndex = courseUnits?.findIndex(
                    (u) => u.id === params.unitId,
                  );
                  if (
                    unitIndex !== undefined &&
                    unitIndex < (courseUnits?.length || 0) - 1
                  ) {
                    navigate(
                      `/classroom/${params.id}/unit/${
                        courseUnits?.[unitIndex + 1].id
                      }/${match?.params.tab ?? "overview"}`,
                    );
                  }
                }}
                icon={<IconPageNextLine />}
              />
            </Toolbar>
          </PageHead>
          <Tabs
            value={match?.params.tab ?? "overview"}
            onChange={(value) => navigate(value === "overview" ? "." : value)}
          >
            <Tabs.TabItem value="overview">Overview</Tabs.TabItem>
            <Tabs.TabItem value="activities">Assessments</Tabs.TabItem>
            <Tabs.TabItem value="resources">Resources</Tabs.TabItem>
          </Tabs>
          {props.classroom && (
            <>
              {(match?.params.tab ?? "overview") === "overview" && unit && (
                <ClassroomUnitOverview
                  classroom={props.classroom}
                  unit={unit}
                />
              )}
              {(match?.params.tab ?? "overview") === "activities" && unit && (
                <ClassroomUnitActivities
                  classroom={props.classroom}
                  unit={unit}
                />
              )}
              {(match?.params.tab ?? "overview") === "resources" && unit && (
                <ClassroomUnitResources
                  classroom={props.classroom}
                  unit={unit ?? undefined}
                />
              )}
            </>
          )}
        </>
      }
      side={<ClassroomUnitSidebar classroom={props.classroom} unit={unit} />}
    />
  );
};

export default ClassroomUnitRoute;
